import {
  createStockClass,
  deleteMultipleCapTableTransactions,
  updateStockClass,
  upsertMultipleCapTableTransactions,
} from '@commenda/api';
import { DirectorReturnType } from '@commenda/api/director/useGetAllDirectorsInCompany';
import { useToggle } from '@commenda/kakashi';
import {
  AuthorizedSharesType,
  SerializedCapTableTransactionType,
  SerializedStockClass,
  TUpsertMultipleTransactionsParams,
} from '@commenda/types/src/cap-table/types';
import { CorporationDataType } from '@commenda/types/src/corporation';
import {
  DirectorBase,
  TEquityInfoKS,
  TValidateStakeholdersResponse,
} from '@commenda/types/src/incorporation/types';
import { getCorporationName } from '@commenda/utils';
import {
  CorporationType,
  Country,
  Currency,
  StakeholderType,
  StockClassType,
} from '@prisma/client';
import { useEffect, useState } from 'react';
import { useNotification } from '../../../../hooks/useNotification';
import { calculateStakeholdersEquityMap } from '../../common/corporateStakeholder/utils';
import { validateEquityInfo } from '../../common/corporateStakeholder/validators';
import { getDirectorName } from '../../Director/common';
import { getCaptableData } from '../common';
import { TCapTableDataItem, TShareholderVsSharePercentageMap } from '../types';

export const useEquityFormState = ({
  capTableTransactions,
  stockClasses,
  directorsData,
  corporations,
  mutateCorporations,
  resetEditModalPresetState,
  corporationId,
  onSubmitSuccess,
  validateStakeholdersAndGetIdsWithError,
  config,
  corporationType,
  incorporationCountry,
}: {
  capTableTransactions: SerializedCapTableTransactionType[];
  stockClasses: SerializedStockClass[];
  directorsData: DirectorReturnType[];
  corporations: CorporationDataType[];
  mutateCorporations: () => void;
  resetEditModalPresetState: () => void;
  corporationId: number;
  onSubmitSuccess: () => void;
  validateStakeholdersAndGetIdsWithError: (
    selectedStakeholders: TShareholderVsSharePercentageMap,
    {
      directors,
      corporations,
      corporationId,
    }: {
      directors: DirectorBase[];
      corporations: CorporationDataType[];
      corporationId: number;
    }
  ) => TValidateStakeholdersResponse[];
  config: {
    defaultShareCount: number;
    defaultSharePrice: number;
    currency: Currency;
  };
  corporationType?: CorporationType;
  incorporationCountry?: Country;
}) => {
  const { setNotification } = useNotification();
  const [equityInfo, setEquityInfo] = useState<TEquityInfoKS>({
    shareCount: null,
    price: null,
    shareRetained: null,
  });
  const [selectedStakeholders, setSelectedStakeholders] =
    useState<TShareholderVsSharePercentageMap>();

  const [individualShareholderToEdit, setIndividualShareholderToEdit] =
    useState<DirectorBase>();
  const [corporateShareholderToEdit, setCorporateShareholderToEdit] =
    useState<number>();
  const [hasESOPs, setHasESOPs] = useState<boolean>(false);
  const [stakeholderIdsWithError, setStakeholderIdsWithError] = useState<
    Set<string>
  >(new Set());
  const [capTableTransactionIdsToDelete, setCapTableTransactionIdsToDelete] =
    useState<string[]>([]);

  const [isSubmitting, setSubmitting] = useState(false);

  const [capTableData, setCapTableData] = useState<TCapTableDataItem[]>([]);

  const stakeholderTypeModalVisibility = useToggle();
  const corporateStakeholderModalVisibility = useToggle();
  const directorModalVisibility = useToggle();

  // we can pick the first stock class here on the corporation level, as when the incorporation process is ongoing
  // we will only have one stock class
  const stockClass = stockClasses?.[0];

  const selectAddedShareholder = ({
    id,
    name,
    type,
  }: {
    type: StakeholderType;
    id: number;
    name: string;
  }) => {
    const selectedShareholdersCopy = { ...selectedStakeholders };

    const addedShareholderId =
      type === StakeholderType.INSTITUTION
        ? `${StakeholderType.INSTITUTION}_${id}`
        : `${StakeholderType.INDIVIDUAL}_${id}`;

    if (addedShareholderId in selectedShareholdersCopy) {
      selectedShareholdersCopy[addedShareholderId] = {
        ...selectedShareholdersCopy[addedShareholderId],
        name,
      };
    } else {
      selectedShareholdersCopy[addedShareholderId] = {
        capTableTransactionId: '',
        name,
        sharePercentage: 0,
        id,
        shareCount: 0,
        type,
        stakeholderId:
          type === StakeholderType.INDIVIDUAL
            ? directorsData?.find((dir) => dir.id === +id)?.stakeholder?.id
            : corporations?.find((corp) => corp.id === id)?.stakeholder?.id,
      };
    }
    setSelectedStakeholders(selectedShareholdersCopy);
    setStakeholderIdsWithError((prevIds) => {
      const newIds = new Set(prevIds);
      newIds.delete(id.toString());
      return newIds;
    });
  };

  const onChangeShareholder = (selectedShareholders: string[]) => {
    const stakeholders: TShareholderVsSharePercentageMap = {
      ...selectedStakeholders,
    };
    let existingStakeholders = Object.keys(stakeholders);

    selectedShareholders.forEach((shareholder) => {
      if (!existingStakeholders.includes(shareholder)) {
        // This shareholder is not in the selectedStakeholders, so we need to add it
        const [type, idStr] = shareholder.split('_');
        stakeholders[shareholder] = {
          id: +idStr,
          name:
            type === StakeholderType.INDIVIDUAL
              ? getDirectorName(directorsData?.find((dir) => dir.id === +idStr))
              : getCorporationName(
                  corporations?.find((corp) => corp.id === +idStr)
                ),
          shareCount: 0,
          sharePercentage: 0,
          type: type as StakeholderType,
          capTableTransactionId: '',
          stakeholderId:
            type === StakeholderType.INDIVIDUAL
              ? directorsData?.find((dir) => dir.id === +idStr)?.stakeholder?.id
              : corporations?.find((corp) => corp.id === +idStr)?.stakeholder
                  ?.id,
        };
      }
    });

    existingStakeholders = Object.keys(stakeholders);

    existingStakeholders.forEach((vl) => {
      if (!selectedShareholders.includes(vl)) {
        const capTableTransactionId = stakeholders[vl]?.capTableTransactionId;
        if (capTableTransactionId) {
          setCapTableTransactionIdsToDelete((prev) => [
            ...prev,
            capTableTransactionId,
          ]);
        }
        setStakeholderIdsWithError((prev) => {
          const newSet = new Set(prev);
          newSet.delete(vl);
          return newSet;
        });
        delete stakeholders[vl];
      }
    });

    setSelectedStakeholders(stakeholders);
  };

  const onAddCorporateShareholder = async ({
    corporateStakeholderId,
    corporationName,
  }: {
    corporateStakeholderId: number;
    corporationName: string;
  }) => {
    corporateStakeholderModalVisibility.hide();
    stakeholderTypeModalVisibility.hide();
    await mutateCorporations();
    selectAddedShareholder({
      type: StakeholderType.INSTITUTION,
      id: corporateStakeholderId,
      name: corporationName,
    });
    resetEditModalPresetState();
    setCorporateShareholderToEdit(undefined);
    setStakeholderIdsWithError((prev) => {
      const newSet = new Set(prev);
      newSet.delete(`INSTITUTION_${corporateStakeholderId}`);
      return newSet;
    });
  };

  const onEditShareholderDetails = (stakeholderId: string) => {
    const [type, idStr] = stakeholderId.split('_');

    if (type === StakeholderType.INSTITUTION) {
      setCorporateShareholderToEdit(+idStr);
      corporateStakeholderModalVisibility.show();
    } else if (type === StakeholderType.INDIVIDUAL) {
      const selectedDirector = directorsData?.find((dir) => dir.id === +idStr);
      setIndividualShareholderToEdit(selectedDirector);
      directorModalVisibility.show();
    }
  };

  const onSubmit = async () => {
    setSubmitting(true);

    const validationResultWithErrors = validateEquityInfo(
      selectedStakeholders,
      equityInfo.shareCount,
      equityInfo.price
    );

    if (validationResultWithErrors) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: validationResultWithErrors.message,
      });
      setSubmitting(false);
      return;
    }

    const invalidStakeholders = validateStakeholdersAndGetIdsWithError(
      selectedStakeholders,
      {
        directors: directorsData || [],
        corporations,
        corporationId,
      }
    );

    if (invalidStakeholders?.length) {
      setStakeholderIdsWithError(
        new Set(
          invalidStakeholders.map((stakeholder) => stakeholder.id.toString())
        )
      );

      // Use specific email error message if available, otherwise use generic message
      const errorMessage =
        (invalidStakeholders as any).emailErrorMessage ||
        'Please fill in the missing information for shareholders by clicking on "Edit".';

      setNotification({
        show: true,
        valid: false,
        message: errorMessage,
        title: 'Error',
      });
      setSubmitting(false);
      return;
    }

    let stockClassId: string | undefined;

    if (!stockClass) {
      // first generate stock class, assign it to corporation
      const createStockClassResponse = await createStockClass({
        corporationId,
        name: 'Common Shares',
        type: StockClassType.COMMON,
        additionalInformation: {
          authorizedShares: equityInfo.shareCount,
          authorizedSharesType: AuthorizedSharesType.DEFAULT,
          pricePerShare: {
            amount: equityInfo.price,
            currency: config.currency,
          },
        },
      });

      if (!createStockClassResponse.ok) {
        setNotification({
          show: true,
          valid: false,
          title: 'Error',
          message:
            'Something went wrong, please try again or contact Team Commenda if you need help.',
        });
        setSubmitting(false);
        return;
      }

      const createdStockClass = await createStockClassResponse.json();

      stockClassId = createdStockClass.id;
    } else {
      // update the existing stock class
      const updateStockClassResponse = await updateStockClass(stockClass.id, {
        additionalInformation: {
          authorizedShares: equityInfo.shareCount,
          authorizedSharesType: AuthorizedSharesType.DEFAULT,
          pricePerShare: {
            amount: equityInfo.price,
            currency: config.currency,
          },
        },
      });

      if (!updateStockClassResponse.ok) {
        setNotification({
          show: true,
          valid: false,
          title: 'Error',
          message:
            'Something went wrong, please try again or contact Team Commenda if you need help.',
        });
        setSubmitting(false);
        return;
      }

      const updatedStockClass = await updateStockClassResponse.json();
      stockClassId = updatedStockClass.id;
    }

    if (capTableTransactionIdsToDelete.length) {
      const deleteMultipleCapTableTransactionsResponse =
        await deleteMultipleCapTableTransactions({
          capTableTransactionIdsToDelete,
        });

      if (!deleteMultipleCapTableTransactionsResponse.ok) {
        setNotification({
          show: true,
          valid: false,
          title: 'Error',
          message:
            'Something went wrong, while deleting transactions, please try again or contact Team Commenda if you need help.',
        });
        return;
      }
    }

    const transactions: TUpsertMultipleTransactionsParams = {
      corporationIdToBuySharesIn: corporationId,
      stockClassId,
      transactions: Object.values(selectedStakeholders || {}).map(
        (stakeholder) => {
          const relatedCapTableItem = capTableData.find(
            (td) =>
              td.id ===
              (stakeholder.type === StakeholderType.INDIVIDUAL
                ? `INDIVIDUAL_${stakeholder.id}`
                : `INSTITUTION_${stakeholder.id}`)
          );
          return {
            quantity: relatedCapTableItem?.shares
              ? +relatedCapTableItem.shares.split(',').join('') || 0
              : 0,
            ...(stakeholder.type === StakeholderType.INDIVIDUAL
              ? { directorId: stakeholder.id }
              : { corporationId: stakeholder.id }),
            ...(stakeholder.capTableTransactionId
              ? { transactionId: stakeholder.capTableTransactionId }
              : {}),
            ...(stakeholder.stakeholderId
              ? { stakeholderId: stakeholder.stakeholderId }
              : {}),
          };
        }
      ),
    };

    const updateTransactionsResponse =
      await upsertMultipleCapTableTransactions(transactions);

    if (!updateTransactionsResponse.ok) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message:
          'Something went wrong, Please try again later or contact Team Commenda if you need help',
      });
      return;
    }

    setSubmitting(false);
    if (onSubmitSuccess) onSubmitSuccess();
  };

  useEffect(() => {
    if (!stockClasses) return;
    if (!capTableTransactions) return;

    const { shareRetained, stakeholdersEquityMap, authorizedShares } =
      calculateStakeholdersEquityMap({
        capTableTransactions,
        stockClass,
      });

    setEquityInfo({
      shareCount: authorizedShares || config.defaultShareCount,
      price:
        stockClass?.additionalInformation?.pricePerShare?.amount ||
        config.defaultSharePrice,
      shareRetained,
    });

    setHasESOPs(shareRetained > 0 && capTableTransactions?.length > 0);

    if (capTableTransactions?.length > 0)
      setSelectedStakeholders(stakeholdersEquityMap);
  }, [capTableTransactions, stockClasses]);

  useEffect(() => {
    if (!directorModalVisibility.visible)
      setIndividualShareholderToEdit(undefined);
    if (!corporateStakeholderModalVisibility.visible)
      setCorporateShareholderToEdit(undefined);
  }, [
    directorModalVisibility.visible,
    corporateStakeholderModalVisibility.visible,
  ]);

  useEffect(() => {
    setCapTableData(
      getCaptableData({
        equityInfo,
        selectedStakeholders,
        hasESOPs,
        corporationType,
        incorporationCountry,
      })
    );
  }, [
    equityInfo,
    hasESOPs,
    selectedStakeholders,
    capTableTransactions,
    corporationType,
    incorporationCountry,
  ]);

  return {
    equityInfo,
    setEquityInfo,
    selectedStakeholders,
    setSelectedStakeholders,
    individualShareholderToEdit,
    setIndividualShareholderToEdit,
    corporateShareholderToEdit,
    setCorporateShareholderToEdit,
    hasESOPs,
    setHasESOPs,
    stakeholderIdsWithError,
    setStakeholderIdsWithError,
    capTableTransactionIdsToDelete,
    setCapTableTransactionIdsToDelete,
    isSubmitting,
    setSubmitting,
    stakeholderTypeModalVisibility,
    corporateStakeholderModalVisibility,
    directorModalVisibility,
    stockClass,
    selectAddedShareholder,
    onChangeShareholder,
    onAddCorporateShareholder,
    capTableData,
    setCapTableData,
    onSubmit,
    onEditShareholderDetails,
  };
};
